// Registro central de las queries que la app precarga para trabajar offline.
//
// El problema que resuelve: antes el cache se cebaba pantalla por pantalla (había
// que ENTRAR online a /animales, /pesajes, /movimientos y /reportes para que
// quedaran cacheadas). El prefetch bajaba dos blobs (`/sync/snapshot` y
// `/sync/catalogs`) y sembraba las keys a mano en `seedFromSnapshot.js`, pero ese
// seed se mantenía por separado de las pantallas y quedó corto: la lista principal
// de animales lee `["animals", estId, "all"]` (incluye inactivos) y el seed llenaba
// solo la base; `weights` no se sembraba pese a viajar en el snapshot; `movements`
// y `stock` ni siquiera venían del backend.
//
// La cura es no mantener un mapeo paralelo: acá se declara, por cada queryKey, la
// MISMA queryFn que usa la pantalla. Precargar es entonces literalmente "hacer lo
// que haría la pantalla, pero en background", así que la shape cacheada es
// idéntica por construcción y no hay `process*` duplicados que se desincronicen.
//
// LÍMITE: estas queryFn toman el token de la sesión activa (`getUser().token`),
// que está scopeado al establecimiento ACTIVO. No aceptan un token scoped a otro
// establecimiento, así que este registro sólo sirve para el activo; los demás
// siguen precargándose por snapshot+seed (ver prefetch.js).
import { queryKeys } from "./keys";
import { getAll as getAllAnimals } from "$lib/java/animales/animalesback";
import { getAll as getAllWeights } from "$lib/java/pesajes/pesajesback";
import { getAll as getAllMovements } from "$lib/java/movimientos/movimientosback";
import { getAll as getAllHerds } from "$lib/java/rodeos/rodeosback";
import { getAll as getAllLots } from "$lib/java/lotes/lotesback";
import { getAll as getAllBirths } from "$lib/java/nacimientos/nacimientosback";
import { getAll as getAllPregnancyChecks } from "$lib/java/tactos/tactosback";
import { getAll as getAllComments } from "$lib/java/observaciones/observacionesback";
import { getAllServices } from "$lib/java/servicios/serviciosback";
import { getAllEvents, getAllTipos } from "$lib/java/tratamientos/tratamientosback";
import { getStock } from "$lib/java/reportes/reportesback";

/**
 * Las queries a precargar para `estId`, en el mismo formato que consume
 * `queryClient.prefetchQuery`. Cada entrada replica el par (queryKey, queryFn)
 * de la pantalla que la consume — si cambiás uno, cambiá el otro.
 *
 * `label` se usa sólo para los logs de diagnóstico.
 */
export function offlineQueriesFor(estId) {
    return [
        // Lista de animales ACTIVOS: la usan los selectores de animal de todas
        // las pantallas de alta (observaciones, nacimientos, tactos, servicios…).
        { label: "animals", queryKey: queryKeys.animals(estId), queryFn: () => getAllAnimals(estId) },
        // Lista principal de /animales: incluye INACTIVOS (bajas). Es otra key y
        // otra respuesta: el snapshot nunca pudo cubrirla porque manda sólo activos.
        {
            label: "animals:all",
            queryKey: [...queryKeys.animals(estId), "all"],
            queryFn: () => getAllAnimals(estId, null, true, true),
        },
        // Animales para los selectores de SERVICIO: además de los activos incluye
        // los toros del banco de padres. Es otra key porque es otra respuesta —
        // el resto de las altas (movimientos, tactos, tratamientos, pesajes,
        // nacimientos) comparte `queryKeys.animals`, que no los trae.
        {
            label: "animals:withStudBank",
            queryKey: queryKeys.animalsWithStudBank(estId),
            queryFn: () => getAllAnimals(estId, null, false, true),
        },
        { label: "weights", queryKey: queryKeys.weights(estId), queryFn: () => getAllWeights(estId) },
        { label: "movements", queryKey: queryKeys.movements(estId), queryFn: () => getAllMovements(estId) },
        { label: "births", queryKey: queryKeys.births(estId), queryFn: () => getAllBirths(estId) },
        { label: "services", queryKey: queryKeys.services(estId), queryFn: () => getAllServices(estId) },
        { label: "treatments", queryKey: queryKeys.treatments(estId), queryFn: () => getAllEvents(estId) },
        { label: "treatmentTypes", queryKey: queryKeys.treatmentTypes(estId), queryFn: () => getAllTipos(estId) },
        { label: "pregnancyChecks", queryKey: queryKeys.pregnancyChecks(estId), queryFn: () => getAllPregnancyChecks(estId) },
        // Sin argumento a propósito: es lo que hace /observaciones (el backend
        // scopea por el JWT). Pasarle el cabid cambiaría la shape cacheada.
        { label: "observations", queryKey: queryKeys.observations(estId), queryFn: () => getAllComments() },
        { label: "lots", queryKey: queryKeys.lots(estId), queryFn: () => getAllLots(estId) },
        { label: "herds", queryKey: queryKeys.herds(estId), queryFn: () => getAllHerds(estId) },
        // Agregación del backend para /reportes.
        { label: "stock", queryKey: queryKeys.stock(estId), queryFn: () => getStock() },
    ];
}
